import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search } from "lucide-react";

const DOMAINS = ["All", "Computer Science", "AI/ML", "Public Health", "Civil Engineering",
  "Climate Science", "Environment", "Economics", "Education", "Medical Sciences", "Data Science"];

export default function Mentors() {
  const [mentors, setMentors] = useState([]);
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("All");
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (domain && domain !== "All") params.domain = domain;
    const r = await api.get("/mentors", { params });
    setMentors(r.data);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [domain]);
  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div data-testid="mentors-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">MENTORS</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
        <em className="italic text-kb-primary">Domain experts</em> ready to mentor.
      </h1>

      <div className="mt-8 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kb-ink-light" />
          <input
            className="kb-input pl-9"
            placeholder="Search by name, affiliation, expertise…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="mentors-search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {DOMAINS.map((d) => (
            <button
              key={d}
              onClick={() => setDomain(d)}
              className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border transition-colors ${
                domain === d
                  ? "border-kb-primary bg-kb-primary text-white"
                  : "border-slate-200 bg-white text-kb-ink-muted hover:border-kb-primary hover:text-kb-primary"
              }`}
              data-testid={`mentor-filter-${d.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading mentors…</div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger-in">
          {mentors.map((m) => (
            <Link key={m.id} to={`/mentors/${m.id}`} className="kb-card p-6 block hover:border-kb-primary transition-colors" data-testid={`mentor-${m.id}`}>
              <div className="flex items-center gap-4">
                <img src={m.photo_url} alt="" className="w-14 h-14 rounded-full object-cover bg-slate-100" />
                <div className="min-w-0">
                  <div className="font-serif text-lg text-kb-ink leading-tight truncate">{m.name}</div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1 truncate">{m.affiliation}</div>
                </div>
              </div>
              <p className="text-sm text-kb-ink-muted mt-4 line-clamp-2">{m.bio}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {m.expertise.slice(0, 4).map((e) => (
                  <span key={e} className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink bg-slate-100 px-2 py-1 rounded-sm">{e}</span>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-slate-200 grid grid-cols-2 gap-2">
                <div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light">h-index</div>
                  <div className="font-mono text-kb-primary text-lg">{m.h_index}</div>
                </div>
                <div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light">Pubs</div>
                  <div className="font-mono text-kb-primary text-lg">{m.publications_count}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
